import {
  LABEL_INCORRECT,
  LABEL_LEARNING,
  LABEL_MASTERED,
  LABEL_NEW,
  MASTERY_THRESHOLD_LEARNING,
  MASTERY_THRESHOLD_MASTERED,
  SECTION_EMOJIS,
} from "./constants.js";
import { calculateForecast, calculateInsights } from "./forecasting.js";
import { triggerGlitter } from "./glitter.js";
import { els, state } from "./state.js";
import { getQuestionHistory } from "./storage.js";
import { calculateDailyStats, calculateMasteryLevel, calculateSectionStats, sortByRecency } from "./utils.js";

export const showStats = () => {
  state.mode = "stats";
  els.stats.style.display = "block";
  renderStats();
};

export const renderStats = () => {
  renderInsights();
  renderForecast();
  renderSectionStats();
  const data = calculateDailyStats(state.userProgress.history, state.allQuestions);
  els.dailyStats.innerHTML = "";

  if (data.length === 0) {
    els.dailyStats.innerHTML = "<p>No history yet. Start practicing!</p>";
    return;
  }

  data.forEach(day => {
    const div = document.createElement("div");
    const isGood = day.rate >= 60;
    div.className = `stat-item clickable column-layout ${isGood ? "good-day" : "bad-day"}`;
    div.innerHTML = `
      <div class="flex-between">
        <strong>${day.date}</strong>
        <span>${day.rate}% (${day.correct}/${day.total}) <span class="expand-icon">▼</span></span>
      </div>
      <div class="stat-details">
        <div class="details-content">Loading activity...</div>
      </div>
    `;

    div.onclick = () => {
      const details = div.querySelector(".stat-details");
      const icon = div.querySelector(".expand-icon");
      const isExpanded = details.classList.toggle("expanded");
      icon.textContent = isExpanded ? "▲" : "▼";

      if (isExpanded) {
        renderDailyDetails(day.date, details.querySelector(".details-content"));
      }
    };

    els.dailyStats.appendChild(div);
  });
};

const renderInsights = () => {
  const i = calculateInsights(state.userProgress.history, state.allQuestions);
  els.insightsStats.innerHTML = "";

  if (i.totalExamsPassed === 0 && i.currentStreak === 0 && (i.improvement === null || i.improvement <= 0)) {
    return; // Show nothing if no significant milestones yet
  }

  const div = document.createElement("div");
  div.className = "stat-item column-layout good-day"; // Reuse good-day for positive styling
  div.style.backgroundColor = "var(--feedback-bg)";
  div.style.borderLeft = "5px solid var(--primary-color)";

  let html = `<div class="insights-header">🌟 Achievements Unlocked!</div>`;

  const accuracy = i.totalAnswered > 0 ? Math.round((i.totalCorrect / i.totalAnswered) * 100) : 0;
  html += `
    <div class="insight-item mb-8">
      <span class="insight-icon">🎯</span>
      <div>
        Overall Accuracy: <strong>${accuracy}%</strong><br>
        <small class="insight-subtitle">From ${i.totalAnswered} attempts on active questions.</small>
      </div>
    </div>
  `;

  if (i.improvement > 0) {
    html += `
      <div class="insight-item mb-8">
        <span class="insight-icon">📈</span>
        <div>
          Your accuracy improved by <strong>${i.improvement}%</strong> compared to last week!<br>
          <small class="insight-subtitle">Your brain is hard-wiring these answers.</small>
        </div>
      </div>
    `;
  }

  if (i.currentStreak > 0) {
    const daysLabel = i.currentStreak === 1 ? "day" : "days";
    html += `
      <div class="insight-item mb-8">
        <span class="insight-icon">🔥</span>
        <div>
          You have passed the 60% threshold for the last <strong>${i.currentStreak} ${daysLabel}</strong>!<br>
          <small class="insight-subtitle">Keep that streak alive!</small>
        </div>
      </div>
    `;
  }

  if (i.totalExamsPassed > 0) {
    html += `
      <div class="insight-item">
        <span class="insight-icon">🎓</span>
        <div>
          You've answered <strong>${i.totalCorrect}</strong> questions correctly.<br>
          That means you've passed the equivalent of <strong>${i.totalExamsPassed} full exams</strong>!
        </div>
      </div>
    `;
  }

  div.innerHTML = html;
  els.insightsStats.appendChild(div);
  els.insightsStats.style.display = "block";
};

const renderForecast = () => {
  const f = calculateForecast(state.userProgress.history, state.allQuestions);
  const pctSeen = Math.round((f.seenCount / f.totalQuestions) * 100);
  const pctMastered = Math.round((f.masteredCount / f.totalQuestions) * 100);

  const formatEstimate = (days, date) => {
    if (days === Infinity) return "No progress yet";
    if (days <= 0) return "Completed! 🎉";
    return `${days} days (approx. ${date})`;
  };

  els.forecastStats.innerHTML = `
    <div class="stat-item column-layout">
      <div class="flex-between">
        <strong>Total Questions Answered:</strong>
        <span>${f.totalAnswered}</span>
      </div>
    </div>
    
    <div class="stat-item column-layout">
      <div class="flex-between">
        <strong>Questions Seen:</strong>
        <span>${f.seenCount} / ${f.totalQuestions} (${pctSeen}%)</span>
      </div>
      <div class="forecast-details">
        Velocity: ${f.velocitySeen} new/day <br>
        Estimated Completion: <strong>${formatEstimate(f.daysToSeeAll, f.dateToSeeAll)}</strong>
      </div>
    </div>

    <div class="stat-item column-layout">
      <div class="flex-between">
        <strong>Questions Mastered:</strong>
        <span>${f.masteredCount} / ${f.totalQuestions} (${pctMastered}%)</span>
      </div>
      <div class="forecast-details">
        Velocity: ${f.velocityMastered} mastered/day <br>
        Estimated Mastery: <strong>${formatEstimate(f.daysToMasterAll, f.dateToMasterAll)}</strong>
      </div>
    </div>
  `;
};

const renderSectionStats = () => {
  const data = calculateSectionStats(
    state.allQuestions,
    state.userProgress.history,
    MASTERY_THRESHOLD_MASTERED,
    MASTERY_THRESHOLD_LEARNING,
  );
  els.sectionStats.innerHTML = "";

  if (data.length === 0) {
    els.sectionStats.innerHTML = "<p>No data available.</p>";
    return;
  }

  data.forEach(([name, stats]) => {
    const pctMastered = Math.round((stats.mastered / stats.total) * 100);
    const pctLearning = Math.round((stats.learning / stats.total) * 100);
    const pctIncorrect = Math.round((stats.incorrect / stats.total) * 100);
    const pctNew = Math.round((stats.new / stats.total) * 100);

    const emoji = SECTION_EMOJIS[name] || "❓";

    const div = document.createElement("div");
    div.className = "stat-item column-layout clickable";
    div.innerHTML = `
      <div class="section-header">
        <div class="section-title">
          <strong>${emoji} ${name}</strong>
        </div>
        <div class="section-actions">
          <button class="practice-topic-btn" title="Practice this topic">✨ Practice</button>
          <span class="expand-icon">▼</span>
        </div>
      </div>
      <div class="progress-bar-container">
        <div class="progress-bar-fill m-mastered" style="width: ${pctMastered}%;" title="${LABEL_MASTERED} (${stats.mastered})"></div>
        <div class="progress-bar-fill m-learning" style="width: ${pctLearning}%;" title="${LABEL_LEARNING} (${stats.learning})"></div>
        <div class="progress-bar-fill m-review" style="width: ${pctIncorrect}%;" title="${LABEL_INCORRECT} (${stats.incorrect})"></div>
      </div>
      <div class="section-stats-text">
        <span class="m-text-mastered">${pctMastered}% ${LABEL_MASTERED}</span> •
        <span class="m-text-learning">${pctLearning}% ${LABEL_LEARNING}</span> •
        <span class="m-text-review">${pctIncorrect}% ${LABEL_INCORRECT}</span> •
        <span>${pctNew}% ${LABEL_NEW}</span>
      </div>
      <div class="stat-details">
        <div class="details-content">Loading questions...</div>
      </div>
    `;

    div.onclick = (e) => {
      if (e.target.classList.contains("practice-topic-btn")) {
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent("navigate", { detail: `/practice?topic=${encodeURIComponent(name)}` }));
        return;
      }

      const details = div.querySelector(".stat-details");
      const icon = div.querySelector(".expand-icon");
      const isExpanded = details.classList.toggle("expanded");
      icon.textContent = isExpanded ? "▲" : "▼";

      if (isExpanded) {
        renderTopicDetails(name, details.querySelector(".details-content"));
      }
    };

    els.sectionStats.appendChild(div);
  });
};

const renderTopicDetails = (sectionName, container) => {
  const questions = state.allQuestions.filter(q => q.section === sectionName && getQuestionHistory(q.id).length > 0);
  container.innerHTML = "";

  const groups = {
    mastered: [],
    learning: [],
    incorrect: [],
  };

  questions.forEach(q => {
    const history = getQuestionHistory(q.id);
    const mastery = calculateMasteryLevel(history);

    if (mastery >= MASTERY_THRESHOLD_MASTERED) {
      groups.mastered.push(q);
    } else if (mastery >= MASTERY_THRESHOLD_LEARNING) {
      groups.learning.push(q);
    } else {
      groups.incorrect.push(q);
    }
  });

  const renderGroup = (label, qs, type) => {
    if (qs.length === 0) return;
    const h = document.createElement("div");
    h.style.fontWeight = "bold";
    h.style.marginTop = "10px";
    h.style.marginBottom = "5px";
    h.style.fontSize = "0.8rem";
    h.style.textTransform = "uppercase";
    h.style.color = "var(--text-muted)";
    h.textContent = `${label} (${qs.length})`;
    container.appendChild(h);

    qs.forEach(q => {
      const row = document.createElement("div");
      row.className = "question-row clickable";
      row.innerHTML = `
        <span class="mastery-dot ${type}"></span>
        <span class="question-text-span">${q.question}</span>
      `;
      row.onclick = (e) => {
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent("navigate", { detail: `/?q=${q.id}` }));
      };
      container.appendChild(row);
    });
  };

  renderGroup(LABEL_MASTERED, sortByRecency(groups.mastered, state.userProgress.history), "m-mastered");
  renderGroup(LABEL_LEARNING, sortByRecency(groups.learning, state.userProgress.history), "m-learning");
  renderGroup(LABEL_INCORRECT, sortByRecency(groups.incorrect, state.userProgress.history), "m-review");
};

const renderDailyDetails = (dateStr, container) => {
  container.innerHTML = "";
  const activity = [];

  state.allQuestions.forEach((q) => {
    const history = getQuestionHistory(q.id);
    history.forEach((attempt, index) => {
      const attemptDate = new Date(attempt.date).toISOString().split("T")[0];
      if (attemptDate === dateStr) {
        // Calculate mastery level *at the time* (after this attempt)
        const historyAtTime = history.slice(0, index + 1);
        const masteryLevel = calculateMasteryLevel(historyAtTime);

        // Determine type for dot:
        // - m-review if the question is in "Needs Review" (Level 1)
        // - m-learning if in Learning (Level 2-3)
        // - m-mastered if in Mastered (Level 4-5)
        let type = "m-review";
        if (masteryLevel >= MASTERY_THRESHOLD_MASTERED) {
          type = "m-mastered";
        } else if (masteryLevel >= MASTERY_THRESHOLD_LEARNING) {
          type = "m-learning";
        }

        activity.push({
          id: q.id,
          question: q.question,
          type,
          time: attempt.date,
        });
      }
    });
  });

  // Sort reverse chronologically (newest first)
  activity.sort((a, b) => b.time - a.time);

  if (activity.length === 0) {
    container.innerHTML = "<p class='empty-state'>No activity recorded for this day.</p>";
    return;
  }

  activity.forEach((item) => {
    const row = document.createElement("div");
    row.className = "question-row clickable";
    row.innerHTML = `
      <span class="mastery-dot ${item.type}"></span>
      <span class="question-text-span">${item.question}</span>
    `;
    row.onclick = (e) => {
      e.stopPropagation();
      window.dispatchEvent(new CustomEvent("navigate", { detail: `/?q=${item.id}` }));
    };
    container.appendChild(row);
  });
};
